import React, { useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 700px;
  max-width: 90%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  box-sizing: border-box;
`;

const Title = styled.h3`
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  color: #24292f;
`;

const Label = styled.label`
  display: block;
  margin: 12px 0 4px 0;
  font-size: 13px;
  font-weight: 600;
  color: #24292f;
`;

const Hint = styled.span`
  font-size: 11px;
  color: #586069;
  font-weight: 400;
  margin-left: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5da;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #0969da;
    box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5da;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #0969da;
    box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 20px;
`;

const Button = styled.button`
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const PrimaryButton = styled(Button)`
  background: #0969da;
  color: white;
  
  &:hover {
    background: #0860ca;
  }
`;

const SecondaryButton = styled(Button)`
  background: #f6f8fa;
  color: #24292f;
  border: 1px solid #d1d5da;
  
  &:hover {
    background: #e1e4e8;
  }
`;

const Section = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: #f6f8fa;
  border-radius: 6px;
  border: 1px solid #e1e4e8;
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #24292f;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ParamItem = styled.div`
  background: white;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 8px;
  position: relative;
  border: 1px solid #d1d5da;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  
  & > div {
    min-width: 0;
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: #d1d5da;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 11px;
  cursor: pointer;
  
  &:hover {
    background: #b1b5ba;
  }
`;

const AddButton = styled.button`
  width: 100%;
  padding: 8px;
  background: #f6f8fa;
  border: 1px dashed #d1d5da;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  color: #0969da;
  
  &:hover {
    background: #e1e4e8;
  }
`;

const SmallInput = styled(Input)`
  margin-bottom: 8px;
  box-sizing: border-box;
`;

const SmallSelect = styled(Select)`
  margin-bottom: 8px;
  box-sizing: border-box;
`;

function PromptModal({ componentType, onSubmit, onCancel, spec }) {
  const [values, setValues] = useState(getDefaultValues(componentType));
  const [parameters, setParameters] = useState([]);
  const [requestBody, setRequestBody] = useState({ enabled: false, contentType: 'application/json', schemaType: 'object', required: true });
  const [responses, setResponses] = useState([{ code: '200', description: 'Success', schemaType: 'object', schemaRef: '' }]);
  const [properties, setProperties] = useState([]);
  
  const availableSchemas = spec?.components?.schemas ? Object.keys(spec.components.schemas) : [];
  const isSchemaPrompt = componentType.includes('schema');

  const handleSubmit = () => {
    onSubmit({ ...values, parameters, requestBody, responses, properties });
  };

  const addParameter = () => {
    setParameters([...parameters, { name: '', in: 'path', required: true, type: 'string', description: '', custom: false }]);
  };

  const removeParameter = (index) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const updateParameter = (index, field, value) => {
    const updated = [...parameters];
    updated[index][field] = value;
    setParameters(updated);
  };

  const addResponse = () => {
    setResponses([...responses, { code: '200', description: '', schemaType: 'object', schemaRef: '' }]);
  };

  const removeResponse = (index) => {
    setResponses(responses.filter((_, i) => i !== index));
  };

  const updateResponse = (index, field, value) => {
    const updated = [...responses];
    updated[index][field] = value;
    setResponses(updated);
  };

  const addProperty = () => {
    setProperties([...properties, { name: '', type: 'string', required: false, description: '' }]);
  };

  const removeProperty = (index) => {
    setProperties(properties.filter((_, i) => i !== index));
  };

  const updateProperty = (index, field, value) => {
    const updated = [...properties];
    updated[index][field] = value;
    setProperties(updated);
  };

  const fields = getFieldsForComponent(componentType);
  const isEndpoint = componentType.includes('endpoint');

  return (
    <Overlay onClick={onCancel}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Title>Configure {getComponentTitle(componentType)}</Title>
        
        {fields.map(field => (
          <div key={field.name}>
            <Label>
              {field.label}
              {field.hint && <Hint>{field.hint}</Hint>}
            </Label>
            {field.type === 'select' ? (
              <Select
                value={values[field.name] || ''}
                onChange={(e) => setValues({...values, [field.name]: e.target.value})}
              >
                {field.options.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </Select>
            ) : (
              <Input
                type={field.type || 'text'}
                value={values[field.name] || ''}
                onChange={(e) => setValues({...values, [field.name]: e.target.value})}
                placeholder={field.placeholder}
              />
            )}
          </div>
        ))}
        
        {isEndpoint && (
          <Section>
            <SectionTitle>📋 Parameters</SectionTitle>
            {parameters.map((param, index) => (
              <ParamItem key={index}>
                <RemoveButton onClick={() => removeParameter(index)}>✕</RemoveButton>
                <Row>
                  <div>
                    <Label>Name</Label>
                    {param.in === 'header' ? (
                      param.custom ? (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <SmallInput
                            placeholder="X-Custom-Header"
                            value={param.name}
                            onChange={(e) => updateParameter(index, 'name', e.target.value)}
                            style={{ flex: 1 }}
                          />
                          <button
                            onClick={() => updateParameter(index, 'custom', false)}
                            style={{ padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}
                          >
                            ↩
                          </button>
                        </div>
                      ) : (
                        <SmallSelect
                          value={param.name}
                          onChange={(e) => {
                            if (e.target.value === 'custom') {
                              updateParameter(index, 'custom', true);
                              updateParameter(index, 'name', '');
                            } else {
                              updateParameter(index, 'name', e.target.value);
                            }
                          }}
                        >
                          <option value="">-- Select Header --</option>
                          <option value="Authorization">Authorization</option>
                          <option value="Content-Type">Content-Type</option>
                          <option value="Accept">Accept</option>
                          <option value="X-API-Key">X-API-Key</option>
                          <option value="X-Request-ID">X-Request-ID</option>
                          <option value="X-Correlation-ID">X-Correlation-ID</option>
                          <option value="User-Agent">User-Agent</option>
                          <option value="Accept-Language">Accept-Language</option>
                          <option value="Cache-Control">Cache-Control</option>
                          <option value="If-None-Match">If-None-Match</option>
                          <option value="If-Modified-Since">If-Modified-Since</option>
                          <option value="custom">✏️ Custom Header...</option>
                        </SmallSelect>
                      )
                    ) : (
                      <SmallInput
                        placeholder="id"
                        value={param.name}
                        onChange={(e) => updateParameter(index, 'name', e.target.value)}
                      />
                    )}
                  </div>
                  <div>
                    <Label>Location</Label>
                    <SmallSelect
                      value={param.in}
                      onChange={(e) => {
                        updateParameter(index, 'in', e.target.value);
                        if (e.target.value === 'path') {
                          updateParameter(index, 'required', true);
                        }
                      }}
                    >
                      <option value="path">Path</option>
                      <option value="query">Query</option>
                      <option value="header">Header</option>
                      <option value="cookie">Cookie</option>
                    </SmallSelect>
                  </div>
                </Row>
                <Row>
                  <div>
                    <Label>Type</Label>
                    <SmallSelect
                      value={param.type}
                      onChange={(e) => updateParameter(index, 'type', e.target.value)}
                    >
                      <option value="string">String</option>
                      <option value="integer">Integer</option>
                      <option value="number">Number</option>
                      <option value="boolean">Boolean</option>
                      <option value="array">Array</option>
                    </SmallSelect>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '24px' }}>
                      <input
                        type="checkbox"
                        checked={param.required}
                        onChange={(e) => updateParameter(index, 'required', e.target.checked)}
                      />
                      Required
                    </label>
                  </div>
                </Row>
                <Label>Description</Label>
                <SmallInput
                  placeholder="Parameter description"
                  value={param.description}
                  onChange={(e) => updateParameter(index, 'description', e.target.value)}
                />
              </ParamItem>
            ))}
            <AddButton onClick={addParameter}>+ Add Parameter</AddButton>
          </Section>
        )}
        
        {isEndpoint && (
          <Section>
            <SectionTitle>📤 Request Body</SectionTitle>
            <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', fontWeight: '500' }}>
              <input
                type="checkbox"
                checked={requestBody.enabled}
                onChange={(e) => setRequestBody({...requestBody, enabled: e.target.checked})}
              />
              Include Request Body
            </label>
            {requestBody.enabled && (
              <ParamItem>
                <Row>
                  <div>
                    <Label>Content Type</Label>
                    <SmallSelect
                      value={requestBody.contentType}
                      onChange={(e) => setRequestBody({...requestBody, contentType: e.target.value})}
                    >
                      <option value="application/json">application/json</option>
                      <option value="application/x-www-form-urlencoded">form-urlencoded</option>
                      <option value="multipart/form-data">multipart/form-data</option>
                      <option value="application/xml">application/xml</option>
                    </SmallSelect>
                  </div>
                  <div>
                    <Label>Schema Type</Label>
                    <SmallSelect
                      value={requestBody.schemaType}
                      onChange={(e) => setRequestBody({...requestBody, schemaType: e.target.value})}
                    >
                      <option value="object">Object</option>
                      <option value="array">Array</option>
                      <option value="string">String</option>
                    </SmallSelect>
                  </div>
                </Row>
                <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '8px' }}>
                  <input
                    type="checkbox"
                    checked={requestBody.required}
                    onChange={(e) => setRequestBody({...requestBody, required: e.target.checked})}
                  />
                  Required
                </label>
              </ParamItem>
            )}
          </Section>
        )}
        
        {isEndpoint && (
          <Section>
            <SectionTitle>📥 Responses</SectionTitle>
            {responses.map((response, index) => (
              <ParamItem key={index}>
                {responses.length > 1 && <RemoveButton onClick={() => removeResponse(index)}>✕</RemoveButton>}
                <Row>
                  <div>
                    <Label>Status Code</Label>
                    <SmallInput
                      placeholder="200"
                      value={response.code}
                      onChange={(e) => updateResponse(index, 'code', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Schema Type</Label>
                    <SmallSelect
                      value={response.schemaType}
                      onChange={(e) => updateResponse(index, 'schemaType', e.target.value)}
                    >
                      <option value="object">Object</option>
                      <option value="array">Array</option>
                      <option value="string">String</option>
                      <option value="none">No Content</option>
                    </SmallSelect>
                  </div>
                </Row>
                {response.schemaType === 'object' && availableSchemas.length > 0 && (
                  <div>
                    <Label>Use Existing Schema <Hint>(optional)</Hint></Label>
                    <SmallSelect
                      value={response.schemaRef}
                      onChange={(e) => updateResponse(index, 'schemaRef', e.target.value)}
                    >
                      <option value="">-- Inline schema --</option>
                      {availableSchemas.map(schema => (
                        <option key={schema} value={schema}>{schema}</option>
                      ))}
                    </SmallSelect>
                  </div>
                )}
                <Label>Description</Label>
                <SmallInput
                  placeholder="Successful response"
                  value={response.description}
                  onChange={(e) => updateResponse(index, 'description', e.target.value)}
                />
              </ParamItem>
            ))}
            <AddButton onClick={addResponse}>+ Add Response</AddButton>
          </Section>
        )}
        
        {isSchemaPrompt && values.type === 'object' && (
          <Section>
            <SectionTitle>📝 Properties</SectionTitle>
            {properties.map((prop, index) => (
              <ParamItem key={index}>
                <RemoveButton onClick={() => removeProperty(index)}>✕</RemoveButton>
                <Row>
                  <div>
                    <Label>Property Name</Label>
                    <SmallInput
                      placeholder="email"
                      value={prop.name}
                      onChange={(e) => updateProperty(index, 'name', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <SmallSelect
                      value={prop.type}
                      onChange={(e) => updateProperty(index, 'type', e.target.value)}
                    >
                      <option value="string">String</option>
                      <option value="integer">Integer</option>
                      <option value="number">Number</option>
                      <option value="boolean">Boolean</option>
                      <option value="array">Array</option>
                      <option value="object">Object</option>
                    </SmallSelect>
                  </div>
                </Row>
                <Label>Description</Label>
                <SmallInput
                  placeholder="User email address"
                  value={prop.description}
                  onChange={(e) => updateProperty(index, 'description', e.target.value)}
                />
                <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '8px' }}>
                  <input
                    type="checkbox"
                    checked={prop.required}
                    onChange={(e) => updateProperty(index, 'required', e.target.checked)}
                  />
                  Required
                </label>
              </ParamItem>
            ))}
            <AddButton onClick={addProperty}>+ Add Property</AddButton>
          </Section>
        )}
        
        <ButtonGroup>
          <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSubmit}>Add to Spec</PrimaryButton>
        </ButtonGroup>
      </Modal>
    </Overlay>
  );
}

function getComponentTitle(type) {
  const titles = {
    'get-endpoint': 'GET Endpoint',
    'post-endpoint': 'POST Endpoint',
    'put-endpoint': 'PUT Endpoint',
    'delete-endpoint': 'DELETE Endpoint',
    'patch-endpoint': 'PATCH Endpoint',
    'options-endpoint': 'OPTIONS Endpoint',
    'head-endpoint': 'HEAD Endpoint',
    'param-path': 'Path Parameter',
    'param-query': 'Query Parameter',
    'param-header': 'Header Parameter',
    'param-cookie': 'Cookie Parameter',
    'response-200': '200 Response',
    'response-201': '201 Response',
    'response-400': '400 Response',
    'response-401': '401 Response',
    'response-404': '404 Response',
    'response-500': '500 Response',
    'string-schema': 'String Schema',
    'object-schema': 'Object Schema',
    'array-schema': 'Array Schema',
    'number-schema': 'Number Schema',
    'integer-schema': 'Integer Schema',
    'boolean-schema': 'Boolean Schema',
  };
  return titles[type] || 'Component';
}

function getDefaultValues(type) {
  if (type.includes('endpoint')) {
    return { path: '/resource', summary: '', description: '', operationId: '', tags: '' };
  }
  if (type.includes('param')) {
    return { name: '', description: '', required: 'true', type: 'string', example: '' };
  }
  if (type.includes('response')) {
    return { description: '', schemaType: 'object' };
  }
  if (type.includes('schema')) {
    const schemaType = type.replace('-schema', '');
    return { name: 'MySchema', type: schemaType, description: '', example: '' };
  }
  return {};
}

function getFieldsForComponent(type) {
  if (type.includes('endpoint')) {
    return [
      { name: 'path', label: 'Path', placeholder: '/users' },
      { name: 'summary', label: 'Summary', placeholder: 'Get all users' },
      { name: 'description', label: 'Description', placeholder: 'Returns a list of users' },
      { name: 'operationId', label: 'Operation ID', placeholder: 'getUsers' },
      { name: 'tags', label: 'Tags (comma-separated)', placeholder: 'users, admin' }
    ];
  }
  
  if (type.includes('param')) {
    return [
      { name: 'name', label: 'Parameter Name', placeholder: 'id' },
      { name: 'description', label: 'Description', placeholder: 'User ID' },
      { name: 'required', label: 'Required', type: 'select', options: ['true', 'false'] },
      { name: 'type', label: 'Type', type: 'select', options: ['string', 'integer', 'number', 'boolean', 'array'] },
      { name: 'example', label: 'Example Value', placeholder: '123' }
    ];
  }
  
  if (type.includes('response')) {
    return [
      { name: 'description', label: 'Description', placeholder: 'Successful response' },
      { name: 'schemaType', label: 'Schema Type', type: 'select', options: ['object', 'array', 'string', 'integer', 'boolean'] }
    ];
  }
  
  if (type.includes('schema')) {
    const schemaType = type.replace('-schema', '');
    const examples = {
      string: { placeholder: 'John Doe', description: 'A text value' },
      number: { placeholder: '123.45', description: 'A numeric value' },
      integer: { placeholder: '42', description: 'A whole number' },
      boolean: { placeholder: 'true', description: 'A true/false value' },
      object: { placeholder: '{"id": 1, "name": "John"}', description: 'An object with properties' },
      array: { placeholder: '["item1", "item2"]', description: 'A list of items' }
    };
    const config = examples[schemaType] || examples.object;
    
    return [
      { name: 'name', label: 'Schema Name', placeholder: schemaType.charAt(0).toUpperCase() + schemaType.slice(1) + 'Schema' },
      { name: 'type', label: 'Type', type: 'select', options: ['object', 'array', 'string', 'integer', 'number', 'boolean'] },
      { name: 'description', label: 'Description', placeholder: config.description },
      { name: 'example', label: 'Example', placeholder: config.placeholder }
    ];
  }
  
  return [];
}

export default PromptModal;